<script setup>
import ActionButton from '#elements/ActionButton.vue'
import DebugInfo from '#src/components/Debug/DebugInfo.vue'
import { computed, ref, useAttrs } from 'vue'
import { nanoid } from 'nanoid'
import { useFeaturesStore } from '#stores/useFeaturesStore.js'
import { useVModel } from '@vueuse/core'
import { useI18n } from 'vue-i18n'

defineOptions({ inheritAttrs: false })

const attrs = useAttrs()
const emit = defineEmits([ 'update:modelValue' ])
const features = useFeaturesStore()
const i18n = useI18n()
const props = defineProps({
	allowShow: { type: Boolean, default: false },
	id: { type: String, default: () => nanoid(6) },
	label: { type: String, required: false, default: null },
	modelValue: { type: String, required: false, default: '' },
	noMargin: { type: Boolean, required: false, default: false },
	showIndicators: { type: Boolean, default: false }
})

const internalValue = useVModel(props, 'modelValue', emit)
const showPassword = ref(false)
const isRequired = computed(() => [ '', true ].includes(attrs?.required ?? false))
</script>

<template>
	<div
		:class="{'mbe-3': !noMargin}"
		:data-testid="props.id"
		data-field="password"
		v-bind="null"
	>
		<div
			v-if="label || $slots.action"
			class="d-flex flex-wrap gap-1 mbe-1"
		>
			<label
				v-if="label"
				:for="id"
				class="mie-auto"
			>
				{{ label }}
				<font-awesome-icon
					v-if="isRequired"
					class="text-negative"
					icon="asterisk"
					size="xs"
				/>
				<DebugInfo class="badge badge-small badge-debug mis-2">FormFieldPassword</DebugInfo>
			</label>
			<slot
				v-if="$slots.action"
				class="mis-auto"
				name="action"
			/>
		</div>
		<div class="input-group input-group-fill">
			<slot
				v-if="$slots.prepend"
				name="prepend"
			/>
			<input
				:id="id"
				v-model="internalValue"
				:type="showPassword ? 'text' : 'password'"
				v-bind="$attrs"
			>
			<ActionButton
				v-if="allowShow && features.isActive('handle_failed_logins')"
				:icon="showPassword ? 'eye-slash' : 'eye' "
				hide-text
				@click="showPassword = !showPassword"
			>
				{{ showPassword ? i18n.t('common.action-hide') : i18n.t('common.action-show') }}
			</ActionButton>
			<slot
				v-if="$slots.append"
				name="append"
			/>
		</div>
		<div
			v-if="$slots.help || $slots.info"
			class="d-flex flex-wrap gap-3 mbs-1"
		>
			<div
				v-if="$slots.help"
				class="mie-auto"
			>
				<slot name="help" />
			</div>
			<div
				v-if="$slots.info"
				class="mis-auto"
			>
				<slot name="info" />
			</div>
		</div>
	</div>
</template>
